import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Drawer, Typography, IconButton, Tabs, Tab } from "@material-ui/core";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@material-ui/icons/Close";
import {
  DATA_TAG_DUPLICATE_USER_DATA,
  DATA_TAG_DUPLICATE_FILE_DATA,
  FETCH_USERS_LIST,
  FETCH_FILES_LIST,
} from "../DataTagConstants";
import DuplicateUserDrillDown from "./DuplicateUserDrillDown";
import "./DriveDrawer.scss";
import { SENSITIVITY_LOOKUP } from "./DataTagAllFilesTable";
import Page_Loader from "../../../assets/vizr_images/page_loader.gif";
import DuplicateFileDrillDown from "./DuplicateFileDrillDown";
import MapTab from "./MapTab";
import TableChartIcon from "@material-ui/icons/TableChart";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import FileMapTab from "./FileMapTab";
import DataTagSensitivityFilter from "../Data Tag FIlters/DataTagSensitivityFilter";
import DataTagUserSearch from "../Data Tag FIlters/DataTagUserSearch";
import FilterableHeader from "../Data Tag FIlters/FilterableHeader";
import DataTagPopupUserFilter from "../Data Tag FIlters/DataTagPopupUserFilter";
import FilterableHeaderPopup from "../Data Tag FIlters/FilterableHeaderPopup";

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function formatFileSize(size = 0) {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 ** 3) return `${(size / 1024 ** 2).toFixed(1)} MB`;
  return `${(size / 1024 ** 3).toFixed(1)} GB`;
}

const makeUserColumns = ({ userNameFilter, setUserNameFilter, userList }) => [
  {
    field: "username",
    headerName: "Duplicated By User",
    flex: 1.2,
    sortable: true,
    align: "left",
    headerAlign: "left",
    renderHeader: () => (
      <FilterableHeaderPopup
        title="Duplicated By User"
        isFilterApplied={userNameFilter.length > 0}
        onClear={() => setUserNameFilter([])}
      >
        <DataTagPopupUserFilter
          users={userList}
          selectedValues={userNameFilter}
          onApply={setUserNameFilter}
        />
      </FilterableHeaderPopup>
    ),
    renderCell: ({ row }) => (
      <div className="drawer-user-cell">
        <div className="drawer-user-avatar">{getInitials(row.username)}</div>
        <span className="drawer-user-name">{row.username}</span>
      </div>
    ),
  },
  {
    field: "fileCount",
    headerName: "Copies",
    flex: 1,
    sortable: true,
    align: "center",
    headerAlign: "center",
    renderCell: ({ value }) => (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <span className="drawer-count-badge">{value}</span>
      </div>
    ),
  },
];

function DriveDrawer({ open, onClose, drive, dupInfo, activeDrive, loc }) {
  const dispatch = useDispatch();
  const [userView, setUserView] = useState("map"); // table | map

  const [tab, setTab] = useState(0);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });

  const loader = useSelector((state) => state.dataTagReducer.popupLoader);
  const userList = useSelector((state) => state.dataTagReducer.usersList);
  const fileList = useSelector((state) => state.dataTagReducer.filesList);

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedSensitivity, setSelectedSensitivity] = useState([]);
  const [userNameFilter, setUserNameFilter] = useState([]);
  const [fileNameFilter, setFileNameFilter] = useState([])

  const isUserDrillDown = Boolean(selectedUser);
  const isFileDrillDown = Boolean(selectedFile);

  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id,
  );
  const duplicateUserData = useSelector(
    (state) => state.dataTagReducer.duplicateUserData || {},
  );
  const duplicateFileData = useSelector(
    (state) => state.dataTagReducer.duplicateDriveFileData || {},
  );

  const userRows = duplicateUserData?.content || [];
  const userTotalRows = duplicateUserData?.totalElements || 0;
  const fileRows = duplicateFileData?.content || [];
  const fileTotalRows = duplicateFileData?.totalElements || 0;

  const handleTabChange = (_, value) => {
    setTab(value);
    setPaginationModel({
      page: 0,
      pageSize: 20,
    });
  };
  useEffect(() => {
    if (!dataSourceId || !drive?.enterpriseActorId) return;

    dispatch({
      type: FETCH_USERS_LIST,
      payload: {
        dataSourceId,
        actorId: drive?.enterpriseActorId,
      },
    });

    dispatch({
      type: FETCH_FILES_LIST,
      payload: {
        dataSourceId,
        actorId: drive?.enterpriseActorId
      }
    })
  }, [dispatch, dataSourceId, drive?.enterpriseActorId]);

  // Fetch Users tab data
  useEffect(() => {
    if (
      !open ||
      (tab !== 0 && tab !== 2) ||
      !dataSourceId ||
      !drive?.enterpriseActorId
    ) {
      return;
    }

    dispatch({
      type: DATA_TAG_DUPLICATE_USER_DATA,
      payload: {
        dataSourceId,
        actorId: drive.enterpriseActorId,
        page: paginationModel.page,
        size: paginationModel.pageSize,
        usernameFilter: userNameFilter.length > 0 ? userNameFilter : null,
      },
    });
  }, [
    open,
    tab,
    dataSourceId,
    drive?.enterpriseActorId,
    paginationModel.page,
    paginationModel.pageSize,
    dispatch,
    userNameFilter
  ]);

  // Fetch Files tab data
  useEffect(() => {
    if (
      loc === "usersTable" ||
      !open ||
      tab !== 1 ||
      !dataSourceId ||
      !drive?.enterpriseActorId
    ) {
      return;
    }

    dispatch({
      type: DATA_TAG_DUPLICATE_FILE_DATA,
      payload: {
        dataSourceId,
        actorId: drive.enterpriseActorId,
        page: paginationModel.page,
        size: paginationModel.pageSize,
        sensitivity:
          selectedSensitivity.length > 0 ? selectedSensitivity : null,
        fileNames: fileNameFilter.length > 0 ?fileNameFilter : null,
      },
    });
  }, [
    open,
    tab,
    dataSourceId,
    drive?.enterpriseActorId,
    paginationModel.page,
    paginationModel.pageSize,
    dispatch,
    selectedSensitivity,
    fileNameFilter,
  ]);

  useEffect(() => {
    if (open) {
      setPaginationModel({ page: 0, pageSize: 20 });
      setSelectedUser(null);
      setSelectedFile(null);
      setUserView("map");
    }
  }, [drive?.enterpriseActorId, open]);

  const handlePaginationChange = (model) => {
    setPaginationModel((prev) => ({
      page: prev.pageSize !== model.pageSize ? 0 : model.page,
      pageSize: model.pageSize,
    }));
  };

  const userColumns = useMemo(
    () =>
      makeUserColumns({
        userNameFilter,
        setUserNameFilter,
        userList,
      }),
    [userNameFilter, userList],
  );

  const FILE_COLUMNS = [
  {
    field: "fileName",
    headerName: "File Name",
    flex: 1.5,
    sortable: true,
     renderHeader: () => (
      <FilterableHeaderPopup
        title="File Name"
        isFilterApplied={fileNameFilter.length > 0}
        onClear={() => setFileNameFilter([])}
      >
        <DataTagPopupUserFilter
          users={fileList}
          selectedValues={fileNameFilter}
          onApply={setFileNameFilter}
        />
      </FilterableHeaderPopup>
    ),
    renderCell: ({ value }) => (
      <span
        title={value}
        style={{
          fontSize: 13,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </span>
    ),
  },
  {
    field: "category",
    headerName: "Sensitivity",
    flex: 1.2,
    sortable: true,
    align: "left",
    headerAlign: "left",
    renderCell: (p) => {
      if (!p.value) return "-";
      const info =
        SENSITIVITY_LOOKUP[p.value] || SENSITIVITY_LOOKUP.NO_SENSITIVITY;
      return <span className={`tag-chip ${info.className}`}>{info.label}</span>;
    },
  },
  {
    field: "duplicateCount",
    headerName: "Duplicates",
    flex: 0.8,
    sortable: true,
    align: "center",
    headerAlign: "center",
    renderCell: ({ value }) => (
      <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <span className="drawer-count-badge">{value}</span>
      </div>
    ),
  },
  {
    field: "totalSize",
    headerName: "Size",
    flex: 0.8,
    sortable: true,
    align: "center",
    headerAlign: "center",
    renderCell: ({ value }) => (
      <span
        style={{
          fontSize: 12,
          color: "rgba(0,0,0,0.6)",
        }}
      >
        {formatFileSize(value)}
      </span>
    ),
  },
];

  const userGridRows = userRows.map((item, index) => ({
    id: `${item.duplicateActor?.id}-${item.directory}-${index}`,
    username: item.duplicateActor?.username || "—",
    email: item.duplicateActor?.email || "—",
    directory: item.directory,
    fileCount: item.fileCount,
    duplicateActorId: item.duplicateActor?.id,
    actorId: drive?.enterpriseActorId,
    directory: item.directory,
  }));

  const fileGridRows = fileRows.map((item, index) => ({
    id: item.originalFileId || index,
    fileName: item.fileName || "—",
    category: item.category || "—",
    duplicateCount: item.duplicateCount || 0,
    duplicateUserCount: item.duplicateUserCount || 0,
    totalSize: item.totalSize || 0,
  }));

  const stats = [
    {
      label: "Files",
      value:
        loc === "usersTable"
          ? activeDrive?.noOfFiles || "—"
          : activeDrive?.totalFiles.toLocaleString() || "—",
    },
    {
      label: "PDA",
      value:
        loc === "usersTable"
          ? activeDrive?.noOfPdaFiles || "—"
          : activeDrive?.pdaFiles?.toLocaleString() || "—",
    },
    {
      label: "Size",
      value:
        loc === "usersTable"
          ? activeDrive?.fieldSize || "—"
          : activeDrive?.totalFileSize?.toLocaleString() || "—",
    },
    {
      label: "Copies",
      icon: "/DataTagIcons/file-mutation.svg",
      value:
        dupInfo?.mutationCount != null
          ? dupInfo.mutationCount.toLocaleString()
          : "—",
      variant: "mutation",
    },
    {
      label: "Duplicate Objects",
      icon: "/DataTagIcons/files.svg",
      value:
        dupInfo?.filesDuplicated != null
          ? dupInfo.filesDuplicated.toLocaleString()
          : "—",
      variant: "duplicate",
    },
  ];

  const initials = getInitials(drive?.driveName);

  const handleSensitivityNodeClick = (sensitivity) => {
    setSelectedSensitivity([sensitivity]);
    setUserView("table");
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: "drive-drawer-paper" }}
      disableEnforceFocus
    >
      {isUserDrillDown ? (
        <DuplicateUserDrillDown
          parentDriveName={drive?.driveName}
          selectedUser={selectedUser}
          onBack={() => setSelectedUser(null)}
          onClose={onClose}
        />
      ) : isFileDrillDown ? (
        <DuplicateFileDrillDown
          parentDriveName={drive?.driveName}
          selectedFile={selectedFile}
          onBack={() => setSelectedFile(null)}
          onClose={onClose}
        />
      ) : (
        <>
          <div className="drive-drawer-header">
            <div className="drive-drawer-title-row">
              <div className="drive-drawer-title-left">
                {loc === "usersTable" ? (
                  <div className="drive-drawer-avatar">{initials}</div>
                ) : (
                  <div className="drive-icon">
                    <img
                      src="/DataTagIcons/DataEstateFolder.svg"
                      alt="Folder"
                    />
                  </div>
                )}
                <Typography className="drive-drawer-name">
                  {drive?.driveName || "—"}
                </Typography>
              </div>
              <IconButton
                size="small"
                onClick={onClose}
                aria-label="Close drawer"
                className="drive-drawer-close-btn"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>

            {/* Stat pills */}
            <div className="drive-drawer-stats-row">
              {stats.map(({ label, icon, value, variant }) => (
                <div key={label} className="drive-drawer-stat-pill">
                  <div className="drive-drawer-stat-pill-header">
                    <span className="drive-drawer-stat-label">{label}:</span>
                  </div>
                  <span className="drive-drawer-stat-value">{value}</span>
                </div>
              ))}
            </div>
            <hr className="separatorStyle" />

            <div className="drive-map-table-view-container">
              <Tabs
                value={tab}
                onChange={handleTabChange}
                className="drive-drawer-tabs"
                variant="fullWidth"
              >
                <Tab label="Users" disableRipple />
                {loc !== "usersTable" && <Tab label="Files" disableRipple />}
              </Tabs>
              <div className="user-view-switch">
                <IconButton
                  size="small"
                  onClick={() => setUserView("table")}
                  className={`user-view-btn ${
                    userView === "table" ? "active" : ""
                  }`}
                  title="Table View"
                >
                  <TableChartIcon fontSize="small" />
                </IconButton>

                <IconButton
                  size="small"
                  onClick={() => setUserView("map")}
                  className={`user-view-btn ${
                    userView === "map" ? "active" : ""
                  }`}
                  title="Map View"
                >
                  <AccountTreeIcon fontSize="small" />
                </IconButton>
              </div>
            </div>
          </div>

          <div className="drive-drawer-body">
            {tab === 0 && (
              <>
                {userView === "table" ? (
                  <div className="drive-drawer-grid-wrap">
                    {loader && (
                      <img
                        src={Page_Loader}
                        alt="loading"
                        className="loader-data-source dataTagPopupLoader"
                      />
                    )}

                    <DataGrid
                      rows={userGridRows}
                      columns={userColumns}
                      rowCount={userTotalRows}
                      pagination
                      paginationMode="server"
                      paginationModel={paginationModel}
                      onPaginationModelChange={handlePaginationChange}
                      pageSizeOptions={[5, 10, 20, 50, 100]}
                      onRowClick={({ row }) => setSelectedUser(row)}
                      disableSelectionOnClick
                      disableColumnMenu
                    />
                  </div>
                ) : (
                  <MapTab
                    duplicateUserData={duplicateUserData}
                    drive={drive}
                    onUserClick={(row) => setSelectedUser(row)}
                  />
                )}
              </>
            )}

            {/* Files tab */}
            {loc !== "usersTable" && tab === 1 && (
              <>
                {userView === "table" ? (
                  <div className="drive-drawer-grid-wrap">
                    {loader && (
                      <img
                        src={Page_Loader}
                        alt="loading"
                        className="loader-data-source dataTagPopupLoader"
                      ></img>
                    )}
                    <div className="quick-filter-bar">
                      <span className="quick-filter-title">Sensitivity:</span>
                      <DataTagSensitivityFilter
                        value={selectedSensitivity}
                        onChange={setSelectedSensitivity}
                        loc="UsersTable"
                      />
                    </div>
                    <DataGrid
                      rows={fileGridRows}
                      columns={FILE_COLUMNS}
                      rowCount={fileTotalRows}
                      pagination
                      paginationMode="server"
                      pageSizeOptions={[5, 10, 20, 50, 100]}
                      paginationModel={paginationModel}
                      onRowClick={({ row }) => setSelectedFile(row)}
                      onPaginationModelChange={handlePaginationChange}
                      disableSelectionOnClick
                      disableColumnMenu
                    />
                  </div>
                ) : (
                  <FileMapTab
                    dataSourceId={dataSourceId}
                    actorId={drive?.enterpriseActorId}
                    drive={drive}
                    onSensitivityClick={handleSensitivityNodeClick}
                  />
                )}
              </>
            )}
          </div>
        </>
      )}
    </Drawer>
  );
}

export default DriveDrawer;
